import { normalizeArabic, normalizeDigits } from "./fundCalcChatEngine.js";

const SHEET_ID = "1vtyUxNFQXMnwdwCMS4s-xNSaa13Jt8tp";
const MURABAHA_SHEET = "نسب المرابحه ";
const DEDUCTION_SHEET = "الاستقطاع ";

const BANK_MARKERS = [
  ["ابو ظبي", "أبو ظبي"],
  ["ابوظبي", "أبو ظبي"],
  ["دار التمليك", "دار التمليك"],
  ["تمليك", "دار التمليك"],
  ["مسار النمو", "مسار النمو"],
  ["مسار", "مسار النمو"],
  ["بدايه", "بداية"],
  ["انماء", "الإنماء"],
  ["امارات", "الإمارات"],
  ["راجحي", "الراجحي"],
  ["اهلي", "الأهلي"],
  ["بلاد", "البلاد"],
  ["رياض", "الرياض"],
  ["عربي", "العربي"],
  ["فرنسي", "الفرنسي"],
  ["جزير", "الجزيرة"],
  ["سهل", "سهل"],
  ["ساب", "ساب"],
];

const RANK_BY_COLUMN = {
  2: "جندي",
  3: "جندي أول",
  4: "عريف",
  5: "وكيل رقيب",
  6: "رقيب",
  7: "رقيب أول",
  9: "رئيس رقباء",
  11: "ملازم أول",
  12: "نقيب",
  13: "رائد",
  14: "مقدم",
  15: "عقيد",
  16: "عميد",
  17: "لواء",
};

let tablesPromise = null;

export function loadPolicyTables() {
  if (!tablesPromise) {
    tablesPromise = Promise.all([
      loadGoogleSheet(MURABAHA_SHEET, false),
      loadGoogleSheet(DEDUCTION_SHEET, true),
    ])
      .then(([murabaha, deduction]) => ({ murabaha, deduction }))
      .catch((error) => {
        tablesPromise = null;
        throw error;
      });
  }
  return tablesPromise;
}

export async function lookupBankPolicy(input) {
  const tables = await loadPolicyTables();
  return matchPolicy(tables.murabaha, tables.deduction, input);
}

export function matchPolicy(murabaha, deduction, input) {
  const bankName = input.bankName || "";
  const yearsAsked = Number(input.years) || 30;
  const groups = groupProfitColumns(murabaha.labels || []);
  const group = groups.find((item) => sameBank(item.bank, bankName));
  if (!group) {
    return {
      ok: false,
      summary: `ما لقيت ${bankName || "البنك"} في شيت نسب المرابحة.`,
    };
  }
  const picked = pickVariation(group.columns, input);
  const variation = picked.column;
  const yearRows = (murabaha.rows || []).filter((row) => {
    const year = Number(row[0]);
    return year >= 1 && year <= 40;
  });
  const available = yearRows.map((row) => Number(row[0]));
  const year = nearestYear(available, yearsAsked);
  const yearRow = yearRows.find((row) => Number(row[0]) === year);
  const profit = round2(yearRow && yearRow[variation.index]);
  if (!yearRow || !profit) {
    return {
      ok: false,
      summary: `ما فيه هامش ربح في الشيت لـ ${group.bank} لمدة ${yearsAsked || "غير محددة"} سنة.`,
    };
  }
  const realOnlyColumn = /عقاري فقط/.test(normalizeArabic(variation.label));
  const personal = realOnlyColumn ? 0 : profit;
  const salary = Number(input.salary) || 0;
  const deductionMatch = deductionRate(deduction, bankName, salary, Number(input.supported) === 1);
  if (!deductionMatch || deductionMatch.percent == null) {
    return {
      ok: false,
      summary: `ما لقيت نسبة استقطاع لـ ${group.bank} في شيت الاستقطاع.`,
    };
  }
  const deductionPercent = deductionMatch.percent;
  const retirementAge = retirementForRank(murabaha.rows || [], input.rankName);
  const yearReason = year !== yearsAsked
    ? `الشيت ما فيه صف لـ ${yearsAsked} سنة، فاستخدمت أقرب مدة موجودة وهي ${year} سنة.`
    : `صف ${year} سنة في شيت نسب المرابحة.`;
  const personalReason = realOnlyColumn
    ? "العمود مكتوب عليه عقاري فقط، لذلك هامش الربح الشخصي 0."
    : "العمود ليس مخصصاً للعقار فقط، لذلك هامش الربح الشخصي نفس هامش الربح.";
  const summary = [
    "من شيت سياسات المنتجات البنكية:",
    group.bank,
    "",
    `مدة التمويل: ${year} سنة`,
    `السبب: ${yearReason}`,
    "",
    `هامش الربح: ${formatRate(profit)}%`,
    `السبب: ${picked.reasons.concat([`القيمة من صف ${year} سنة`]).join("، ")}.`,
    "",
    `هامش الربح الشخصي: ${formatRate(personal)}%`,
    `السبب: ${personalReason}`,
    "",
    `الاستقطاع الأول: ${Math.round(deductionPercent * 100)}%`,
    `السبب: ${deductionMatch.reason}`,
    `الاستقطاع الثاني: ${Math.round(deductionPercent * 100)}%`,
    "السبب: نفس نسبة الاستقطاع الأول، من شيت الاستقطاع.",
    retirementAge ? "" : null,
    retirementAge ? `عمر التقاعد: ${retirementAge}` : null,
    retirementAge ? `السبب: رتبة ${input.rankName} في صف أعمار التقاعد بآخر شيت نسب المرابحة عمرها ${retirementAge}.` : null,
  ].filter((line) => line !== null && line !== undefined).join("\n");
  return {
    ok: true,
    profit,
    profitPersonal: personal,
    deduction: deductionPercent,
    years: year,
    retirementAge,
    summary,
  };
}

export function groupProfitColumns(labels) {
  const groups = [];
  let current = null;
  labels.forEach((label, index) => {
    const text = String(label || "").trim();
    if (!text || normalizeArabic(text).includes("مده التمويل")) return;
    const marker = BANK_MARKERS.find(([token]) => normalizeArabic(text).includes(normalizeArabic(token)));
    if (marker) {
      current = { bank: marker[1], columns: [] };
      groups.push(current);
      current.columns.push({ index, label: stripBank(text, marker[0]) || text });
      return;
    }
    if (current) current.columns.push({ index, label: text });
  });
  return groups;
}

function stripBank(text, token) {
  const source = normalizeLoose(text).replace(/غبر/g, "غير");
  const pattern = new RegExp(token.replace(/\s+/g, "\\s*"), "ig");
  return source.replace(pattern, " ").replace(/^ال\s+/, "").replace(/\s+/g, " ").trim();
}

function normalizeLoose(value) {
  return String(value || "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/غبر/g, "غير")
    .trim();
}

function sameBank(sheetBank, requestBank) {
  const left = normalizeArabic(sheetBank).replace(/\s+/g, "");
  const right = normalizeArabic(requestBank).replace(/\s+/g, "");
  if (!left || !right) return false;
  if (left.includes(right) || right.includes(left)) return true;
  return BANK_MARKERS.some(([token]) => {
    const key = normalizeArabic(token).replace(/\s+/g, "");
    return left.includes(key) && right.includes(key);
  });
}

function pickVariation(columns, input) {
  const supported = Number(input.supported) === 1;
  const salary = Number(input.salary) || 0;
  const jobId = Number(input.jobId);
  const uncertified = jobId === 5;
  const privateSector = jobId === 5 || jobId === 6;
  const realOnly = input.financeMix === "real_only";
  const scored = columns.map((column) => {
    const label = normalizeArabic(String(column.label || "").replace(/غبر/g, "غير"));
    let score = 0;
    const both = label.includes("مدعوم وغير");
    const unsupported = !both && (label.includes("غير مدعوم") || label.startsWith("غير مدعوم"));
    const supportedOnly = !both && label.includes("مدعوم") && !unsupported;
    if (both) score += 1;
    else if (supported && supportedOnly) score += 5;
    else if (!supported && unsupported) score += 5;
    else if (supported && unsupported) score -= 5;
    else if (!supported && supportedOnly) score -= 5;
    if (label.includes("اقل") && label.includes("20000")) score += salary < 20000 ? 6 : -6;
    if ((label.includes("اعلى") || label.includes("اعلي")) && label.includes("20000")) score += salary >= 20000 ? 6 : -6;
    if (label.includes("غير معتمد")) score += uncertified ? 7 : -5;
    if (label.includes("حكومي") || label.includes("الشركات المعتمده")) score += privateSector || uncertified ? -5 : 6;
    if (label.includes("قطاع خاص")) score += privateSector || uncertified ? 6 : -5;
    if (label.includes("عقاري فقط")) score += realOnly ? 6 : -4;
    if (label.includes("شخصي") && label.includes("عقاري") && !label.includes("فقط")) score += realOnly ? -3 : 3;
    return { column, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return {
    column: scored[0].column,
    reasons: variationReasons(scored[0].column, input),
  };
}

function variationReasons(column, input) {
  const label = normalizeArabic(String(column.label || "").replace(/غبر/g, "غير"));
  const reasons = [`العمود في شيت نسب المرابحة: «${column.label}»`];
  const supported = Number(input.supported) === 1;
  const salary = Number(input.salary) || 0;
  const jobId = Number(input.jobId);
  const both = label.includes("مدعوم وغير");
  const unsupported = !both && label.includes("غير مدعوم");
  const supportedOnly = !both && label.includes("مدعوم") && !unsupported;
  if (supported && supportedOnly) reasons.push("لأن العميل مدعوم");
  else if (!supported && unsupported) reasons.push("لأن العميل غير مدعوم");
  else if (both) reasons.push("لأن العمود ينطبق على المدعوم وغير المدعوم");
  if (label.includes("اقل") && label.includes("20000") && salary < 20000) {
    reasons.push(`لأن الراتب ${formatMoney(salary)} أقل من 20,000`);
  }
  if ((label.includes("اعلى") || label.includes("اعلي")) && label.includes("20000") && salary >= 20000) {
    reasons.push(`لأن الراتب ${formatMoney(salary)} من 20,000 فأعلى`);
  }
  if (label.includes("غير معتمد") && jobId === 5) reasons.push("لأن جهة العمل قطاع خاص غير معتمد");
  if (label.includes("قطاع خاص") && (jobId === 5 || jobId === 6)) {
    reasons.push(jobId === 6 ? "لأن جهة العمل قطاع خاص معتمد" : "لأن جهة العمل قطاع خاص");
  }
  if ((label.includes("حكومي") || label.includes("الشركات المعتمده")) && jobId !== 5 && jobId !== 6) {
    const jobs = { 1: "عسكري", 2: "مدني", 3: "متقاعد", 4: "شبه حكومي" };
    reasons.push(`لأن جهة العمل ${jobs[jobId] || "حكومي أو جهة معتمدة"}`);
  }
  if (label.includes("عقاري فقط") && input.financeMix === "real_only") reasons.push("لأن نوع التمويل عقاري فقط");
  if (label.includes("شخصي") && label.includes("عقاري") && !label.includes("فقط") && input.financeMix === "both") {
    reasons.push("لأن نوع التمويل شخصي وعقاري");
  }
  return reasons;
}

function nearestYear(years, asked) {
  if (!years.length) return asked;
  if (years.includes(asked)) return asked;
  return years.slice().sort((a, b) => Math.abs(a - asked) - Math.abs(b - asked) || a - b)[0];
}

function retirementForRank(rows, rankName) {
  if (!rankName) return null;
  const ageRow = rows.find((row) => {
    const year = Number(row[0]);
    if (year >= 1 && year <= 40) return false;
    const ages = row.map(Number).filter((value) => value >= 40 && value <= 70);
    return ages.length >= 4;
  });
  if (!ageRow) return null;
  const wanted = normalizeArabic(rankName);
  const found = Object.keys(RANK_BY_COLUMN).find((column) => {
    const name = normalizeArabic(RANK_BY_COLUMN[column]);
    return name === wanted || name.includes(wanted) || wanted.includes(name);
  });
  if (!found) return null;
  const age = Number(ageRow[found]);
  return age >= 40 ? age : null;
}

export function deductionRate(table, bankName, salary, supported) {
  const parsed = parseDeductionTable(table.rows || []);
  if (supported) {
    return {
      percent: parsed.supportedAll,
      reason: "العميل مدعوم، ونص الشيت: الاستقطاع للمدعوم 65% لكل البنوك.",
    };
  }
  const bank = parsed.banks.find((item) => sameBank(item.name, bankName));
  if (!bank) return null;
  const rules = bank.rules.map(parseDeductionRule).filter(Boolean);
  const matches = rules.filter((rule) => ruleMatches(rule, salary));
  if (!matches.length) return null;
  matches.sort((a, b) => ruleSpan(a) - ruleSpan(b));
  const best = matches[0];
  const narrow = matches.length > 1 ? " اختيرت هذه الشريحة لأنها الأضيق بين الشرائح المطابقة." : "";
  return {
    percent: best.percent,
    reason: `${deductionReason(best, salary)}${narrow}`,
  };
}

function deductionReason(rule, salary) {
  const quote = rule.text ? ` نص الشيت: «${rule.text}».` : "";
  if (rule.any) return `النسبة في الشيت لكل الرواتب.${quote}`;
  if (rule.min != null && rule.max != null) {
    return `الراتب ${formatMoney(salary)} داخل الشريحة من ${formatMoney(rule.min)} إلى ${formatMoney(rule.max)}.${quote}`;
  }
  if (rule.max != null && rule.maxExclusive) {
    return `الراتب ${formatMoney(salary)} أقل من ${formatMoney(rule.max)}.${quote}`;
  }
  if (rule.max != null) {
    return `الراتب ${formatMoney(salary)} لا يتجاوز ${formatMoney(rule.max)}.${quote}`;
  }
  if (rule.min != null) {
    return `الراتب ${formatMoney(salary)} من ${formatMoney(rule.min)} فأعلى.${quote}`;
  }
  return quote.trim();
}

function formatMoney(value) {
  return (Number(value) || 0).toLocaleString("en-US");
}

function parseDeductionTable(rows) {
  const banks = [];
  let supportedAll = 0.65;
  rows.forEach((row) => {
    const cells = (row || []).map((cell) => String(cell || "").trim()).filter(Boolean);
    const joined = cells.join(" ");
    if (joined.includes("لكل البنوك")) return;
    if (!cells.length) return;
    const name = cells[0];
    const normalized = normalizeArabic(name);
    if (normalized.includes("جهه التمويل") || normalized.includes("العوده") || normalized === "نسبه الاستقطاع") return;
    if (cells.length < 2) return;
    banks.push({ name, rules: cells.slice(1) });
  });
  return { banks, supportedAll };
}

function parseDeductionRule(text) {
  const raw = String(text || "").replace(/غبر/g, "غير");
  const normalized = normalizeArabic(raw);
  if (!normalized || normalized === ".") return null;
  const percents = [...raw.matchAll(/(\d+(?:\.\d+)?)\s*%/g)].map((match) => Number(match[1]));
  let percent = percents.length ? percents[percents.length - 1] / 100 : null;
  if (percent == null) {
    const bare = raw.match(/(\d+(?:\.\d+)?)\s*استثناء/);
    if (bare) percent = Number(bare[1]) / 100;
  }
  if (percent == null) return null;
  const sheetText = raw.trim();
  const percentNumber = Math.round(percent * 100);
  const salaryNums = [...normalizeDigits(raw).matchAll(/(\d+(?:\.\d+)?)/g)]
    .map((match) => Number(match[1]))
    .filter((value) => Math.round(value) !== percentNumber)
    .map((value) => (value < 1000 ? value * 1000 : value));
  if (normalized.includes("اقل") || normalized.includes("تحت")) {
    return { max: salaryNums[0], maxExclusive: true, percent, text: sheetText };
  }
  if (normalized.includes("اكثر") || normalized.includes("اكتر") || normalized.includes("فوق") || normalized.includes("اعلى")) {
    return { min: salaryNums[0] || 0, percent, text: sheetText };
  }
  if (salaryNums.length >= 2) {
    return { min: Math.min(...salaryNums), max: Math.max(...salaryNums), percent, text: sheetText };
  }
  if (normalized.includes("لكل") || salaryNums.length === 0) return { any: true, percent, text: sheetText };
  return { min: salaryNums[0], percent, text: sheetText };
}

function ruleMatches(rule, salary) {
  if (rule.any) return true;
  if (rule.min != null && salary < rule.min) return false;
  if (rule.max != null && rule.maxExclusive && salary >= rule.max) return false;
  if (rule.max != null && !rule.maxExclusive && salary > rule.max) return false;
  return true;
}

function ruleSpan(rule) {
  if (rule.min != null && rule.max != null) return rule.max - rule.min;
  if (rule.min != null) return 1e12 - rule.min;
  if (rule.max != null) return rule.max;
  return 1e13;
}

function round2(value) {
  const number = Number(value);
  if (!number) return 0;
  return Math.round(number * 100) / 100;
}

function formatRate(value) {
  return String(round2(value));
}

function loadGoogleSheet(sheetName, rawRows) {
  const names = [sheetName, sheetName.trim()];
  return names.reduce((promise, name) => promise.catch(() => requestSheet(name, rawRows)), Promise.reject());
}

function requestSheet(sheetName, rawRows) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("sheet lookup needs a browser"));
      return;
    }
    const callbackName = `fundSheet_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    const script = document.createElement("script");
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("انتهت مهلة قراءة الشيت"));
    }, 15000);
    function cleanup() {
      clearTimeout(timer);
      delete window[callbackName];
      if (script.parentNode) script.parentNode.removeChild(script);
    }
    window[callbackName] = (payload) => {
      cleanup();
      if (!payload || payload.status === "error" || !payload.table) {
        reject(new Error("تعذر قراءة الشيت"));
        return;
      }
      resolve(gvizToTable(payload, rawRows));
    };
    const headers = rawRows ? "&headers=0" : "";
    script.src = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=responseHandler:${callbackName}${headers}&sheet=${encodeURIComponent(sheetName)}`;
    script.onerror = () => {
      cleanup();
      reject(new Error("تعذر قراءة الشيت"));
    };
    document.head.appendChild(script);
  });
}

function gvizToTable(payload, rawRows) {
  const labels = (payload.table.cols || []).map((column) => column.label || "");
  const rows = (payload.table.rows || []).map((row) => (row.c || []).map((cell) => {
    if (!cell || cell.v == null) return "";
    return cell.v;
  }));
  if (rawRows && labels.some((label) => String(label).trim())) {
    rows.unshift(labels);
  }
  return { labels, rows };
}
